import { useMemo, useState } from "react";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatSkeleton } from "@/components/chat/ChatSkeleton";
import { SubagentElapsed } from "@/components/chat/SubagentElapsed";
import { SubagentRunResult } from "@/components/chat/SubagentRunResult";
import { SubagentStopButton } from "@/components/chat/SubagentStopButton";
import { ToolEntryLabel } from "@/components/chat/ToolEntryLabel";
import { ToolEntry } from "@/components/chat/ToolEntry";
import {
	isFailedSubagentStatus,
	hasMeasurableElapsed,
	isTerminalSubagentStatus,
	subagentProfileLabel,
	subagentStatusText,
	subagentTotalInputTokens,
	type ChatSubagentRun,
} from "@/lib/chat";
import { formatTokenCount } from "@/lib/format-datetime";
import { groupBlocks, isLastInTurn, projectMessages } from "@/lib/chat-render";
import { resolveDisplayedSubagentRun } from "@/lib/subagent-tasks";
import { useSubagentTranscript } from "./useSubagentTranscript";

/**
 * run 1 件の実行履歴。run 一覧に行が無くても (取得前・取得失敗) tool_result 由来の runId だけで
 * 開けるよう、run は任意にする。届くまではヘッダーを省いて実行履歴だけを見せる。
 */
export function SubagentTaskDetail({ runId, run }: { runId: string; run?: ChatSubagentRun }) {
	const { detail, error } = useSubagentTranscript(runId);
	const currentRun = resolveDisplayedSubagentRun(run, detail, error);
	// run はポーリングごとに差し替わる (カウンタが進む) ため、実行履歴の再構築は
	// messages と状態が実際に変わったときだけに絞る。
	const messages = detail?.messages;
	const runStatus = currentRun?.status;
	const blocks = useMemo(
		() =>
			messages
				? groupBlocks(
						projectMessages(messages, {
							pendingUserMessageId: null,
							waitingToolUseId: null,
							subagentRunStatus: runStatus,
						}),
					)
				: [],
		[messages, runStatus],
	);
	const tools = useMemo(
		() => blocks.flatMap((block) => (block.kind === "tool_group" ? block.tools : [])),
		[blocks],
	);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const toggle = (key: string) =>
		setExpanded((previous) => {
			const next = new Set(previous);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	// 実行中は今動いているツールを活動サマリーにする。終端後は SubagentRunResult が結果を描く。
	// そのターンの tool_use は実行前に全件まとめて保存され、tool_result はターンの最後に 1 通で入る。
	// つまり実行中ターンのツールは全件 "running" に見えるので、状態からは実行中の 1 件を選べない。
	// toolCallCount は完了したツール数なので、これがそのまま実行中ツールの 0 始まりの位置になる。
	const activeTool = tools.at(currentRun?.toolCallCount ?? 0) ?? tools.at(-1);
	const activity =
		currentRun && !isTerminalSubagentStatus(currentRun.status) ? (activeTool ?? null) : null;
	return (
		<div
			data-testid="subagent-task-detail"
			className="flex h-full min-h-0 flex-col overflow-y-auto p-3"
		>
			{currentRun && (
				<>
					<div className="flex min-w-0 items-center gap-2">
						<span
							className={`text-caption ${isFailedSubagentStatus(currentRun.status) ? "text-danger" : "text-fg-2"}`}
						>
							{subagentStatusText(currentRun.status)}
						</span>
						<span
							title={currentRun.description}
							data-tooltip-if-truncated
							className="min-w-0 flex-1 truncate text-ui font-semibold"
						>
							{currentRun.description}
						</span>
						<SubagentStopButton run={currentRun} />
					</div>
					{activity && (
						<ToolEntryLabel
							tool={activity}
							testId="subagent-activity"
							className="mt-1 block min-w-0 truncate text-caption text-fg-2"
						/>
					)}
					<div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-caption text-fg-2">
						<span>
							{subagentProfileLabel(currentRun.profile)} · {currentRun.model} ·{" "}
							<RunElapsed run={currentRun} />
						</span>
						<span>
							{currentRun.turns}ターン · 入力{" "}
							{formatTokenCount(subagentTotalInputTokens(currentRun))} / 出力{" "}
							{formatTokenCount(currentRun.outputTokens)} · {currentRun.toolCallCount}ツール
						</span>
						{currentRun.isBackground && <span className="font-mono">実行 ID {currentRun.id}</span>}
					</div>
					{isTerminalSubagentStatus(currentRun.status) && (
						<div className="mt-3 grid gap-2 border-t border-line-1 pt-3">
							{/* ここが全文の表示先なので、取得できている間は誘導の文言を出さない。取得に
							    失敗している間は全文を出せないため、切り詰めの断り書きをそこで止めない。 */}
							<SubagentRunResult
								run={currentRun}
								fullTextHint={error ? "実行履歴を取得できないため、全文を表示できません。" : ""}
							/>
						</div>
					)}
				</>
			)}
			<div className="mt-3 grid gap-1 border-t border-line-1 pt-3">
				{error && <p className="m-0 text-caption text-danger">実行履歴を取得できませんでした。</p>}
				{!error && !detail && <ChatSkeleton />}
				{!error && detail && blocks.length === 0 && (
					<p className="m-0 text-caption text-fg-3">出力はまだありません</p>
				)}
				{blocks.map((block, index) => {
					if (block.kind === "tool_group")
						return block.tools.map((tool) => (
							<ToolEntry
								key={tool.key}
								tool={tool}
								expanded={expanded.has(tool.key)}
								onToggle={() => toggle(tool.key)}
							/>
						));
					// 実行履歴は user/assistant のみ保存されるため compaction/system_notice は現れない。
					if (block.kind !== "text") return null;
					// ユーザー発話は指示文だけ (ツール結果は text ブロックにならない)。別枠で見せる。
					return block.role === "user" ? (
						<div key={block.key} className="mb-1 rounded-3 bg-surface-2 p-1">
							<span className="text-section text-fg-3">指示</span>
							{/* workflow のノードは起動後、ここが prompt を読める唯一の場所。body の
							    user-select: none を外さないと選択もコピーもできない。 */}
							<p className="m-0 select-text whitespace-pre-wrap wrap-anywhere text-caption leading-body">
								{block.text}
							</p>
						</div>
					) : (
						// compact でこのペインの他の行と同じ text-caption に揃える。外すと発言だけ text-ui になる。
						<ChatMessage
							key={block.key}
							message={block}
							isLastInTurn={isLastInTurn(block, blocks[index + 1])}
							compact
						/>
					);
				})}
			</div>
		</div>
	);
}

/** 経過を測れない run (終端したのに endedAt が無い) は伏せ字にする。 */
function RunElapsed({ run }: { run: ChatSubagentRun }) {
	if (!hasMeasurableElapsed(run)) return <>-</>;
	return <SubagentElapsed run={run} />;
}
